import { useEffect, useRef, useState } from "react";
import { HostStripeMessageSchema, stripeBridgeEvent, stripeLaunchUrl } from "@froppio/host-auth";
import { apiBaseUrl } from "@/core/api/apiConfig";
import { authService } from "@/core/auth/services/authService";
import { useWorkspaceOperation } from "@/shared/hooks/useWorkspaceOperation";
import { stripeApiService } from "../services/stripeApiService";

export function useStripeConnect(businessId: string, locationId: string, userId: string) {
  const captureOperation = useWorkspaceOperation();
  const pending = useRef<{ id: string; controller: AbortController; isCurrent: () => boolean; timeout?: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    const restored = (event: PageTransitionEvent) => {
      const current = pending.current;
      if (!event.persisted || authService.kind === "host" || !current?.isCurrent()) return;
      current.controller.abort();
      window.clearTimeout(current.timeout);
      pending.current = null;
      setBusy(false);
      setMessage("Returned from your browser. Check the current status before continuing setup.");
    };
    const receive = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;
      const parsed = HostStripeMessageSchema.safeParse(event.detail);
      if (!parsed.success || !("id" in parsed.data) || parsed.data.id !== pending.current?.id || !pending.current.isCurrent()) return;
      window.clearTimeout(pending.current.timeout);
      pending.current = null;
      setBusy(false);
      setMessage(parsed.data.type === "opened"
        ? "Continue in your browser. Return to Froppio to check the actual account status."
        : "Couldn't open your browser. Please try again.");
    };
    window.addEventListener(stripeBridgeEvent, receive);
    window.addEventListener("pageshow", restored);
    return () => {
      window.removeEventListener(stripeBridgeEvent, receive);
      window.removeEventListener("pageshow", restored);
      pending.current?.controller.abort();
      window.clearTimeout(pending.current?.timeout);
      pending.current = null;
    };
  }, [businessId, locationId, userId]);

  const connect = async () => {
    if (pending.current) return;
    const isCurrent = captureOperation();
    const current = { id: crypto.randomUUID(), controller: new AbortController(), isCurrent, timeout: undefined as number | undefined };
    pending.current = current;
    setBusy(true);
    setMessage("");
    try {
      const native = authService.kind === "host";
      const dto = await stripeApiService.startOnboarding(businessId, { location_id: locationId, channel: native ? "native" : "web" }, current.controller.signal);
      if (pending.current !== current || !isCurrent()) return;
      const url = stripeLaunchUrl(dto.onboarding_url, apiBaseUrl, import.meta.env.DEV);
      if (native) {
        if (!window.ReactNativeWebView) throw new Error("Native bridge unavailable.");
        current.timeout = window.setTimeout(() => {
          if (pending.current !== current || !isCurrent()) return;
          pending.current = null;
          setBusy(false);
          setMessage("Your app could not open onboarding. Update Froppio or try again.");
        }, 15_000);
        window.ReactNativeWebView.postMessage(JSON.stringify({ channel: stripeBridgeEvent, version: 1, type: "open", id: current.id, userId, url }));
      } else {
        if (window.self !== window.top) throw new Error("Onboarding cannot open in a frame.");
        window.location.assign(url);
      }
    } catch {
      if (pending.current !== current || !isCurrent()) return;
      window.clearTimeout(current.timeout);
      pending.current = null;
      setBusy(false);
      setMessage("Couldn't start Stripe setup. Refresh the status and try again.");
    }
  };
  return { connect, busy, message };
}
